import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../hooks/usePageMeta';
import { 
  ArrowLeft, 
  ArrowRight, 
  Calendar, 
  Clock, 
  User, 
  Share2,
  Linkedin,
  Twitter,
  Facebook,
  Copy,
  CheckCircle,
  Tag
} from 'lucide-react';
import { getPostBySlug, getRelatedPosts, blogPosts } from '../data/blogData';

// Full article content generator based on category and title
const generateArticleContent = (post) => {
  const sections = [
    {
      heading: 'Introduction',
      content: `In today's rapidly evolving digital landscape, understanding ${post.title.toLowerCase().replace(/[:\-]/g, '')} is crucial for business success. This comprehensive guide will walk you through everything you need to know to implement effective strategies and achieve measurable results.

Whether you're a startup founder, marketing professional, or business owner, the insights shared here are based on real-world experience helping 150+ businesses achieve growth.`
    },
    {
      heading: 'Why This Matters in 2026',
      content: `The digital marketing landscape has transformed dramatically. With AI-powered tools, changing consumer behaviors, and new platform features, businesses that don't adapt will fall behind.

Key statistics to consider:
• 75% of businesses now use AI in their marketing stack
• Mobile traffic accounts for over 60% of all web visits
• Video content generates 1200% more shares than text and images combined
• Voice search now represents 30% of all searches

Understanding these trends is essential for staying competitive in your market.`
    },
    {
      heading: 'Step-by-Step Implementation Guide',
      content: `Here's a practical framework you can implement immediately:

Step 1: Audit Your Current Position
Before implementing any new strategy, understand where you stand. Analyze your current metrics, identify gaps, and benchmark against competitors.

Step 2: Set Clear, Measurable Goals
Define what success looks like for your business. Use SMART goals—Specific, Measurable, Achievable, Relevant, and Time-bound.

Step 3: Develop Your Strategy
Based on your audit and goals, create a tailored strategy that aligns with your resources and target audience.

Step 4: Execute with Consistency
Implementation is where most businesses fail. Create a clear timeline, assign responsibilities, and maintain consistency.

Step 5: Measure and Optimize
Use data to guide decisions. Track your KPIs regularly and be willing to adjust your approach based on results.`
    },
    {
      heading: 'Common Mistakes to Avoid',
      content: `Based on our experience working with 150+ clients, here are the most common pitfalls:

1. Trying to Do Everything at Once
Focus on mastering one channel before expanding. Depth beats breadth.

2. Ignoring Data
Make decisions based on data, not assumptions. Even small businesses have access to powerful analytics tools.

3. Inconsistent Execution
Success requires sustained effort. Short-term campaigns rarely deliver lasting results.

4. Neglecting Mobile Experience
With over 60% of traffic coming from mobile devices, a poor mobile experience will kill your conversions.

5. Not Testing
A/B testing isn't optional anymore. Small changes can lead to significant improvements in conversion rates.`
    },
    {
      heading: 'Tools and Resources',
      content: `Here are the tools we recommend for implementing these strategies:

Analytics & Tracking:
• Google Analytics 4 (free)
• Google Search Console (free)
• Hotjar for heatmaps

SEO Tools:
• Ahrefs or SEMrush for keyword research
• Screaming Frog for technical audits
• Surfer SEO for content optimization

Marketing Automation:
• HubSpot (free tier available)
• Mailchimp for email marketing
• Zapier for workflow automation

AI Tools:
• ChatGPT for content ideation
• Jasper for AI writing assistance
• Canva for design`
    },
    {
      heading: 'Real Results: Case Study',
      content: `Let's look at a real example from our client portfolio:

Client: E-commerce business in the lifestyle segment
Challenge: Low organic traffic, high customer acquisition costs
Solution: Implemented comprehensive SEO + content strategy

Results after 12 months:
• Organic traffic increased by 340%
• Revenue grew from ₹41K to ₹17 Lac per month
• 300+ keywords ranking on page 1
• Customer acquisition cost reduced by 45%

The key was focusing on high-intent keywords and creating content that addressed customer pain points at every stage of the buying journey.`
    },
    {
      heading: 'Key Takeaways',
      content: `To summarize the main points from this guide:

✅ Start with a clear understanding of your current position and goals
✅ Focus on one channel at a time and master it before expanding
✅ Use data to guide every decision
✅ Consistency is more important than perfection
✅ Always optimize for mobile users
✅ Test continuously and iterate based on results
✅ Invest in the right tools to scale your efforts

Remember, digital marketing success doesn't happen overnight. It requires strategic planning, consistent execution, and continuous optimization.`
    },
    {
      heading: 'Next Steps',
      content: `Ready to implement these strategies for your business? Here's what to do next:

1. Get a Free Growth Audit - Let our team analyze your current digital presence and identify quick wins

2. Download Our Free Resources - Access templates, checklists, and guides on our resources page

3. Schedule a Strategy Call - Discuss your specific challenges with our experts

Digital Startup has helped over 150 businesses achieve measurable growth. We'd love to help you too.`
    }
  ];

  return sections;
};

export const BlogPost = ({ onOpenAuditModal }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getPostBySlug(slug);
  const relatedPosts = getRelatedPosts(slug, 3);
  const [copied, setCopied] = React.useState(false);
  
  usePageMeta(post ? {
    title: `${post.title} | Digital Startup`,
    description: post.excerpt,
    keywords: post.keywords ? post.keywords.join(', ') : ''
  } : {});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#1A1A1A] mb-4">Article Not Found</h1>
          <p className="text-[#525252] mb-6">The article you're looking for doesn't exist.</p>
          <Button asChild className="bg-[#FFAA17] hover:bg-[#E69500] rounded-full">
            <Link to="/insights">Back to Insights</Link>
          </Button>
        </div>
      </main>
    );
  }

  const articleContent = generateArticleContent(post);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Find next and previous posts
  const currentIndex = blogPosts.findIndex(p => p.slug === slug);
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

  return (
    <main className="pt-20 bg-[#F9FAFB]" data-testid={`blog-post-${slug}`}>
      {/* Hero Section */}
      <section className="py-12 md:py-16 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-[#525252] mb-6">
            <Link to="/" className="hover:text-[#FFAA17]">Home</Link>
            <span>/</span>
            <Link to="/insights" className="hover:text-[#FFAA17]">Insights</Link>
            <span>/</span>
            <span className="text-[#1A1A1A]">{post.category}</span>
          </div>

          {/* Category Badge */}
          <span className="inline-block bg-[#FFAA17] text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
            {post.category}
          </span>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A1A1A] mb-6 leading-tight">
            {post.title}
          </h1>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-[#525252] mb-6">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {post.date}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {post.readTime}
            </span>
          </div>

          {/* Share Buttons */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#525252]">Share:</span>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 bg-[#0077B5] text-white rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
              aria-label="Share on LinkedIn"
            >
              <Linkedin className="w-4 h-4" />
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 bg-[#1DA1F2] text-white rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
              aria-label="Share on Twitter"
            >
              <Twitter className="w-4 h-4" />
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 bg-[#1877F2] text-white rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
              aria-label="Share on Facebook"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <button
              onClick={handleCopyLink}
              className="w-8 h-8 bg-[#2E2E2E] text-white rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
              aria-label="Copy link"
            >
              {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-[300px] md:h-[400px] object-cover rounded-2xl"
          />
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <article className="prose prose-lg max-w-none">
            {articleContent.map((section, index) => (
              <div key={index} className="mb-10">
                <h2 className="text-2xl font-bold text-[#1A1A1A] mb-4">{section.heading}</h2>
                <div className="text-[#525252] leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </div>
            ))}
          </article>

          {/* Keywords/Tags */}
          <div className="mt-10 pt-8 border-t border-gray-200">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag className="w-4 h-4 text-[#525252]" />
              {post.keywords.map((keyword, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-[#F2F2F2] text-[#525252] text-sm rounded-full"
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Box */}
      <section className="py-12 bg-[#F2F2F2]">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="bg-[#FFAA17] rounded-2xl p-8 md:p-12 text-center text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Ready to Implement These Strategies?
            </h3>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              Get a free growth audit and discover how Digital Startup can help you achieve similar results.
            </p>
            <Button
              onClick={onOpenAuditModal}
              className="bg-white text-[#FFAA17] hover:bg-gray-100 rounded-full px-8 py-3"
              data-testid="blog-cta"
            >
              Get Free Growth Audit
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 md:px-6 max-w-4xl">
            <h3 className="text-2xl font-bold text-[#1A1A1A] mb-8">Related Articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="group"
                >
                  <div className="bg-[#F9FAFB] rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                    <img
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="w-full h-40 object-cover"
                    />
                    <div className="p-4">
                      <span className="text-xs text-[#FFAA17] font-semibold">
                        {relatedPost.category}
                      </span>
                      <h4 className="text-sm font-semibold text-[#1A1A1A] mt-1 group-hover:text-[#FFAA17] transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h4>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Post Navigation */}
      <section className="py-8 bg-[#F9FAFB] border-t border-gray-200">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="flex justify-between items-center">
            {prevPost ? (
              <Link
                to={`/blog/${prevPost.slug}`}
                className="flex items-center gap-2 text-[#525252] hover:text-[#FFAA17] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Previous Article</span>
              </Link>
            ) : (
              <div />
            )}
            
            <Link
              to="/insights"
              className="text-[#2F80ED] hover:underline font-medium"
            >
              All Articles
            </Link>

            {nextPost ? (
              <Link
                to={`/blog/${nextPost.slug}`}
                className="flex items-center gap-2 text-[#525252] hover:text-[#FFAA17] transition-colors"
              >
                <span className="hidden sm:inline">Next Article</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <div />
            )}
          </div>
        </div>
      </section>

      {/* Article Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": post.title,
            "description": post.excerpt,
            "image": post.image,
            "author": {
              "@type": "Person",
              "name": post.author
            },
            "publisher": {
              "@type": "Organization",
              "name": "Digital Startup",
              "logo": {
                "@type": "ImageObject",
                "url": "https://digitalstartup.in/logo.png"
              }
            },
            "datePublished": post.date,
            "keywords": post.keywords.join(", ")
          })
        }}
      />
    </main>
  );
};

export default BlogPost;
